import useInsurancePlanCopy from '@/catalog/hooks/use-insurance-plan-copy';
import { InsurancePlan } from '@/catalog/types';
import Spacer from '@/common/components/spacer';
import { cn } from '@/shadcn-ui/utils';
import InsuranceDetailsDrawerDialog from '../../insurance-details-drawer-dialog';
import PDPPlanInfoModalTrigger from '../../pdp-plan-info-modal-trigger';
import PlanRadioGroup from '../plan-radio-group/plan-radio-group';
import usePlanGuidePopover, {
  PlanGuidePopoverConfig,
} from '@/catalog/hooks/use-plan-guide-popover';
import GuidePopover from '@/common/components/guide-popover';
import { CreditConfig } from './mileage-plan-selection';

interface InsurancePlanSelectionProps {
  // Insurance plans
  insurancePlans?: InsurancePlan[];
  selectedInsurancePlanId?: string;
  enableInsuranceChange?: boolean;
  handleSelectInsurancePlan?: (value: string) => void;

  // Additional config
  errorConfig: PlanGuidePopoverConfig;
  hasSelectedMileagePlan?: boolean;
  isOrderable: boolean;
  inSwapFlow?: boolean;
  popoverTriggerRef?: React.RefObject<HTMLDivElement | null>;
  creditConfig: CreditConfig;
  hasNoSelectedAgeCreditRange: boolean;
}

export default function InsurancePlanSelection({
  // Insurance plans
  insurancePlans,
  selectedInsurancePlanId,
  enableInsuranceChange,
  handleSelectInsurancePlan,

  // Additional config
  errorConfig,
  hasSelectedMileagePlan,
  isOrderable,
  inSwapFlow,
  popoverTriggerRef,
  creditConfig,
  hasNoSelectedAgeCreditRange,
}: InsurancePlanSelectionProps) {
  // In the swap flow, we need to make sure the radio buttons aren't disabled or else they won't get included in the form submission.
  const disableInsurancePlanSelection =
    !inSwapFlow &&
    (!hasSelectedMileagePlan || !isOrderable || hasNoSelectedAgeCreditRange);
  const { showPopover, error } = usePlanGuidePopover({ errorConfig });
  const { insurancePlansCopy } = useInsurancePlanCopy({
    insurancePlans,
    disableInsurancePlanSelection,
    inSwapFlow,
    selectedInsurancePlanId,
  });

  const handleClick = () => {
    // When insurance plan buttons are disabled due to no credit age range selection, clicking them should open the credit age change drawer
    if (!hasNoSelectedAgeCreditRange) {
      return;
    }

    creditConfig.handleOpen();
  };

  return (
    <>
      {/* Insurance plan selection header */}
      <div className="flex flex-col gap-[4px]">
        <GuidePopover
          show={showPopover}
          triggerComponent={
            <div
              className="flex items-center justify-between gap-[8px]"
              ref={popoverTriggerRef}
            >
              <h3
                className={cn('font-sans-tight', {
                  'text-flexcar-neutral70': disableInsurancePlanSelection,
                })}
              >
                {inSwapFlow ? 'Your protection' : 'Protection'} plan
              </h3>

              <InsuranceDetailsDrawerDialog
                className={cn('h-fit', {
                  'opacity-40': disableInsurancePlanSelection,
                })}
                enableInsuranceChange={enableInsuranceChange}
                variant={inSwapFlow ? 'swap' : undefined}
                triggerVariant="custom"
                customTrigger={
                  <PDPPlanInfoModalTrigger
                    helpSmallClassName="h-[24px] w-[24px]"
                    disabled={disableInsurancePlanSelection}
                  />
                }
                insurancePlans={insurancePlans}
              />
            </div>
          }
          content={error}
        />

        {!inSwapFlow ? (
          <p
            className={cn('body2-tight', {
              'text-flexcar-neutral70': disableInsurancePlanSelection,
            })}
          >
            Basic liability insurance included with both plans.
          </p>
        ) : null}
      </div>

      <Spacer variant="height16" />

      {/* Insurance plan radio group */}
      <PlanRadioGroup
        plans={insurancePlansCopy}
        name="insurance_plan_id"
        selectedPlanId={selectedInsurancePlanId}
        disabled={disableInsurancePlanSelection}
        onValueChange={handleSelectInsurancePlan}
        onClick={handleClick}
      />
    </>
  );
}
